import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { dataListsType } from "../shared/type";
import { isPrerender } from "../utils/ga";
import { formatDistanceKm, LatLng } from "../utils/geo";
import {
  FALLBACK_STORE_IMAGE,
  getStoreLogoUrl,
  getStorePhotoUrl,
} from "../utils/storeImage";
import { getOpeningHoursStatus } from "../utils/openingHours";
import { stylesForMapTheme, type MapTheme } from "../utils/mapStyles";

type Props = {
  stores: dataListsType[];
  selectedStoreId?: number;
  onMarkerClick?: (storeId: number) => void;
  userPosition?: LatLng | null;
  nearbyRadiusKm?: number;
  storeDistances?: Record<number, number>;
  /** Visual theme for the base map tiles. Default: night. */
  mapTheme?: MapTheme;
};

type MapLatLng = { lat: number; lng: number };

const DEFAULT_CENTER = { lat: 35.681236, lng: 139.767125 };

const GOOGLE_MAPS_SCRIPT_ID = "gmaps";
const SCRIPT_URL = "/api/google-maps/js";
const GEO_URL = "/api/google-maps/geocode";
const INFO_WINDOW_STYLE_ID = "regional-store-map-iw-style";
const ADDRESS_SESSION_KEY = "regionalStoreAddressCache.v1";

let scriptPromise: Promise<void> | null = null;

function readAddressSessionCache(): Map<string, string> {
  const map = new Map<string, string>();
  if (typeof sessionStorage === "undefined") return map;
  try {
    const raw = sessionStorage.getItem(ADDRESS_SESSION_KEY);
    if (!raw) return map;
    const parsed = JSON.parse(raw) as Record<string, string>;
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === "string" && value) map.set(key, value);
    });
  } catch {
    // ignore corrupt session payload
  }
  return map;
}

function writeAddressSessionCache(cache: Map<string, string>) {
  if (typeof sessionStorage === "undefined") return;
  try {
    const obj: Record<string, string> = {};
    cache.forEach((value, key) => {
      obj[key] = value;
    });
    sessionStorage.setItem(ADDRESS_SESSION_KEY, JSON.stringify(obj));
  } catch {
    // quota / private mode
  }
}

function addressCacheKey(store: dataListsType): string {
  return store.placeId || `id:${store.id}`;
}

/** Blue teardrop pin matching default Google Maps marker shape. */
const buildUserLocationIcon = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="56" viewBox="0 0 40 56">
    <path fill="#1a73e8" stroke="#ffffff" stroke-width="2" d="M20 1C10.61 1 3 8.61 3 18c0 11.25 17 35 17 35s17-23.75 17-35C37 8.61 29.39 1 20 1z"/>
    <circle cx="20" cy="18" r="7" fill="#ffffff"/>
  </svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getUserLocationMarkerIcon = () => {
  if (!window.google?.maps?.Size || !window.google?.maps?.Point) {
    return undefined;
  }

  return {
    url: buildUserLocationIcon(),
    scaledSize: new window.google.maps.Size(28, 40),
    anchor: new window.google.maps.Point(14, 40),
  };
};

/** Hide Google Maps InfoWindow empty header spacer; keep close button top-right */
/** Hide Google Maps InfoWindow empty header spacer; keep close button top-right */
const injectInfoWindowStyles = () => {
  let style = document.getElementById(
    INFO_WINDOW_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = INFO_WINDOW_STYLE_ID;
    document.head.appendChild(style);
  }

  style.textContent = `
    .gm-style .gm-style-iw-c {
      padding: 0 !important;
      outline: none !important;
    }
    .gm-style .gm-style-iw-t,
    .gm-style .gm-style-iw-tc,
    .gm-style .gm-style-iw-d,
    .gm-style .gm-style-iw {
      outline: none !important;
    }
    .gm-style .gm-style-iw-c:focus,
    .gm-style .gm-style-iw-c:focus-visible,
    .gm-style .gm-style-iw-d:focus,
    .gm-style .gm-style-iw-d:focus-visible,
    .gm-style .gm-style-iw:focus,
    .gm-style .gm-style-iw:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
    .gm-style .gm-style-iw-ch {
      display: none !important;
    }
    .gm-style .gm-style-iw-chr {
      position: absolute !important;
      top: 0 !important;
      right: 0 !important;
      width: 40px !important;
      height: 40px !important;
      z-index: 1 !important;
    }
    .gm-style .gm-style-iw-chr .gm-ui-hover-effect {
      top: 4px !important;
      right: 4px !important;
      width: 32px !important;
      height: 32px !important;
    }
    .gm-ui-hover-effect span {
      margin: 5px !important;
    }
    .gm-style .gm-style-iw-d {
      overflow-x: hidden !important;
      overflow-y: auto !important;
      max-height: 300px !important;
      padding: 12px 40px 12px 14px !important;
      box-sizing: border-box !important;
      scrollbar-width: none !important; /* Firefox */
      -ms-overflow-style: none !important; /* IE/Edge legacy */
    }
    .gm-style .gm-style-iw-d::-webkit-scrollbar {
      width: 0 !important;
      height: 0 !important;
      display: none !important;
    }
  `;
};

const tuneInfoWindowDom = () => {
  requestAnimationFrame(() => {
    const iw = document.querySelector(".gm-style-iw");
    if (!iw) return;

    const headerSpacer = iw.querySelector(".gm-style-iw-ch");
    if (headerSpacer instanceof HTMLElement) {
      headerSpacer.style.display = "none";
    }

    const content = iw.querySelector(".gm-style-iw-d");
    if (content instanceof HTMLElement) {
      content.style.overflowX = "hidden";
      content.style.overflowY = "auto";
      content.style.maxHeight = "300px";
      content.style.padding = "12px 40px 12px 14px";
      content.style.outline = "none";
      content.style.setProperty("scrollbar-width", "none");
      content.style.setProperty("-ms-overflow-style", "none");
    }

    const bubble = iw.querySelector(".gm-style-iw-c");
    if (bubble instanceof HTMLElement) {
      bubble.style.outline = "none";
    }

    if (iw instanceof HTMLElement) {
      iw.style.outline = "none";
    }
  });
};

/** ================= SCRIPT ================= */
const loadScript = () => {
  if (window.google?.maps) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const el = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (el) {
      el.addEventListener("load", () => resolve(), { once: true });
      el.addEventListener("error", () => reject(), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.id = GOOGLE_MAPS_SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;

    document.head.appendChild(s);
  });

  return scriptPromise;
};

const waitMap = () =>
  new Promise<void>((res) => {
    const t = () => {
      if (window.google?.maps?.Map) return res();
      requestAnimationFrame(t);
    };
    t();
  });

/** ================= GEO CACHE ================= */
const geoCache = new Map<string, MapLatLng | null>();

const geocode = async (q: string): Promise<MapLatLng | null> => {
  if (geoCache.has(q)) return geoCache.get(q)!;

  const res = await fetch(`${GEO_URL}?address=${q}`);
  if (!res.ok) return null;

  const data = await res.json();
  const loc = data?.results?.[0]?.geometry?.location;

  const result = loc?.lat && loc?.lng ? { lat: loc.lat, lng: loc.lng } : null;

  geoCache.set(q, result);
  return result;
};

/** ================= UTIL ================= */
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildFallbackAddress = (store: dataListsType) =>
  [store.district, store.prefecture, store.nation].filter(Boolean).join(", ");

const PHONE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;margin-top:1px;">
  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
</svg>`;

const LOCATION_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;margin-top:1px;">
  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
  <circle cx="12" cy="10" r="3"/>
</svg>`;

const buildInfoWindowHtml = (
  store: dataListsType,
  formattedAddress: string,
  distanceKm?: number,
) => {
  const distanceLine =
    distanceKm != null
      ? `<div style="font-size:12px;color:#F05A2A;font-weight:600;margin-bottom:4px;">
          ${escapeHtml(formatDistanceKm(distanceKm))} away
        </div>`
      : "";

  const ratingLine =
    store.rating && store.rating > 0
      ? `<div style="font-size:12px;color:#000;margin-bottom:0px;">
          <span style="color:#f59e0b;">★</span>
          ${escapeHtml(String(store.rating))}
          <!--  ${
            store.userRatingsTotal
              ? `<span>(${escapeHtml(String(store.userRatingsTotal))})</span>`
              : ""
          }  
           --> 
        </div>`
      : "";

  const phoneLine = store.phone
    ? `<div style="display:flex;align-items:flex-start;gap:6px;font-size:12px;color:#000;margin-bottom:4px;">
        ${PHONE_ICON_SVG}
        <a href="tel:${escapeHtml(store.phone)}" style="color:#000;text-decoration:none;outline:none;word-break:break-all;">${escapeHtml(store.phone)}</a>
      </div>`
    : "";

  const hoursStatus = getOpeningHoursStatus(store.openingHours);
  const hoursLine = hoursStatus
    ? (() => {
        const statusColor =
          hoursStatus.isOpen === true
            ? "#188038"
            : hoursStatus.isOpen === false
              ? "#d93025"
              : "#5f6368";

        // Static hours row only (weekly dropdown temporarily disabled).
        return `<div style="margin-bottom:4px;font-size:12px;display:flex;align-items:center;gap:6px;">
            <span style="color:${statusColor};font-weight:600;">${escapeHtml(hoursStatus.statusLabel)}</span>
            <span style="color:#70757a;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
              ${escapeHtml(hoursStatus.summaryHours)}
            </span>
          </div>`;
      })()
    : "";

  const tagsLine =
    store.tags && store.tags.length > 0
      ? `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:2px;margin-top:6px;">
          ${store.tags
            .map(
              (tag) =>
                `<span style="font-size:11px;color:#000;background:#f1f3f4;padding:3px 4px 0;border-radius:4px;">${escapeHtml(tag)}</span>`,
            )
            .join("")}
        </div>`
      : "";

  const storePhotoUrl = getStorePhotoUrl(store.placeId, 400);
  const storeLogoUrl = getStoreLogoUrl(store.storeWebsite);
  const storePhotoLine = storePhotoUrl
    ? `<div style="margin:6px 0 4px;border-radius:8px;overflow:hidden;background:#f1f3f4;height:100px;border: 1px solid #e8e8e8;">
        <img
          src="${escapeHtml(storePhotoUrl)}"
          alt="${escapeHtml(store.title)}"
          loading="lazy"
          draggable="false"
          ondragstart="return false;"
          data-logo="${escapeHtml(storeLogoUrl)}"
          data-fallback="${escapeHtml(FALLBACK_STORE_IMAGE)}"
          style="width:100%;height:100%;object-fit:cover;display:block;-webkit-user-drag:none;user-select:none;"
          onerror="var logo=this.dataset.logo;var fb=this.dataset.fallback;if(this.dataset.step!=='logo'&&logo&&logo!==fb){this.dataset.step='logo';this.src=logo;this.style.objectFit='contain';return;}this.onerror=null;this.src=fb;this.style.objectFit='contain';"
        />
      </div>`
    : storeLogoUrl
      ? `<div style="margin:6px 0 4px;border-radius:8px;overflow:hidden;background:#f1f3f4;height:100px;border: 1px solid #e8e8e8;">
        <img
          src="${escapeHtml(storeLogoUrl)}"
          alt="${escapeHtml(store.title)}"
          draggable="false"
          ondragstart="return false;"
          data-fallback="${escapeHtml(FALLBACK_STORE_IMAGE)}"
          style="width:100%;height:100%;object-fit:contain;display:block;-webkit-user-drag:none;user-select:none;"
          onerror="this.onerror=null;this.src=this.dataset.fallback;this.style.objectFit='contain';"
        />
      </div>`
      : "";

  // const websiteLine = store.storeWebsite
  //   ? `<a href="${escapeHtml(store.storeWebsite)}" target="_blank" rel="noopener noreferrer" style="font-size:12px;color:#1a73e8;text-decoration:none;display:block;margin-top:4px;">
  //       Visit store website
  //     </a>`
  //   : "";

  return `<div style="font-size:14px;line-height:1.5;max-width:260px;font-family:Roboto,Arial,sans-serif;">
    <div style="font-weight:700;font-size:14px;margin-bottom:3px;word-wrap:break-word;overflow-wrap:break-word;white-space:normal;">
      ${escapeHtml(store.title)}
    </div>
    ${distanceLine}
    ${ratingLine}
    ${storePhotoLine}
    ${tagsLine}
    ${hoursLine}
    ${phoneLine}
    <div style="display:flex;align-items:flex-start;gap:6px;font-size:12px;color:#000;margin-bottom:4px;word-wrap:break-word;overflow-wrap:break-word;white-space:normal;">
      ${LOCATION_ICON_SVG}
      <span>${escapeHtml(formattedAddress)}</span>
    </div>
    <div style="border-top:1px solid #e8eaed;padding-top:3px;">
      <a href="${escapeHtml(store.location)}" target="_blank" rel="noopener noreferrer" style="font-size:13px;color:#1a73e8;text-decoration:none;outline:none;font-weight:500;">
        Take me there
      </a>
    
    </div>
  </div>`;
};

const reverseGeocodeLatLng = (lat: number, lng: number): Promise<string> =>
  new Promise((resolve) => {
    if (!window.google?.maps?.Geocoder) {
      resolve("");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat, lng } },
      (results: { formatted_address?: string }[] | null, status: string) => {
        if (status === "OK" && results?.[0]?.formatted_address) {
          resolve(results[0].formatted_address);
          return;
        }
        resolve("");
      },
    );
  });

const parseQuery = (store: dataListsType) => {
  try {
    const url = new URL(store.location);
    return url.searchParams.get("query") || `${store.title} ${store.area}`;
  } catch {
    return `${store.title} ${store.area}`;
  }
};

/** debounce */
const useDebounce = <T,>(value: T, delay = 400) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

/** ================= COMPONENT ================= */
const RegionalStoreMap: React.FC<Props> = ({
  stores,
  selectedStoreId,
  onMarkerClick,
  userPosition = null,
  nearbyRadiusKm,
  storeDistances,
  mapTheme = "night",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<Map<number, any>>(new Map());
  const userMarkerRef = useRef<any>(null);
  const radiusCircleRef = useRef<{ glow: any; main: any } | null>(null);
  // const clusterRef = useRef<any>(null);
  const infoRef = useRef<any>(null);

  const storeGeoCacheRef = useRef(new Map<number, MapLatLng>());
  // Memory + sessionStorage: one reverse-geocode per placeId per browser tab session.
  const addressCacheRef = useRef<Map<string, string>>(
    readAddressSessionCache(),
  );
  const storesByIdRef = useRef(new Map<number, dataListsType>());
  const boundsRef = useRef<any>(null);
  const focusedStoreIdRef = useRef<number | undefined>(undefined);
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;
  const [mapReady, setMapReady] = useState(false);
  const renderLockRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const storeDistancesRef = useRef(storeDistances);
  storeDistancesRef.current = storeDistances;

  const fitMapToStores = useCallback(
    (includeUser = false) => {
      const map = mapRef.current;
      if (!map) return;

      const nextBounds = new window.google.maps.LatLngBounds();
      let hasPoint = false;

      if (includeUser && userPosition) {
        nextBounds.extend(userPosition);
        hasPoint = true;
      }

      markersRef.current.forEach((marker) => {
        const pos = marker.getPosition();
        if (!pos) return;
        nextBounds.extend(pos);
        hasPoint = true;
      });

      if (!hasPoint) return;

      if (includeUser && userPosition && markersRef.current.size === 0) {
        map.setCenter(userPosition);
        map.setZoom(14);
        return;
      }

      map.fitBounds(nextBounds, 60);
    },
    [userPosition],
  );

  const debouncedStores = useDebounce(stores, 400);

  const applyMarkerFocus = useCallback((focusedId?: number) => {
    focusedStoreIdRef.current = focusedId;
    const markers = markersRef.current;

    markers.forEach((marker, id) => {
      if (!focusedId) {
        marker.setVisible(true);
        marker.setOpacity(1);
        marker.setZIndex(undefined);
        marker.setAnimation(null);
        return;
      }

      const isSelected = id === focusedId;
      marker.setVisible(isSelected);
      if (isSelected) {
        marker.setZIndex(window.google.maps.Marker.MAX_ZINDEX + 1);
        marker.setAnimation(window.google.maps.Animation.BOUNCE);
        window.setTimeout(() => marker.setAnimation(null), 1400);
      }
    });
  }, []);

  const focusStoreOnMap = useCallback(
    (store: dataListsType, marker: any, options?: { openInfo?: boolean }) => {
      const map = mapRef.current;
      const info = infoRef.current;
      if (!map || !marker) return;

      const pos = marker.getPosition();
      if (!pos) return;

      applyMarkerFocus(store.id);
      map.panTo(pos);
      const currentZoom = map.getZoom() ?? 12;
      map.setZoom(Math.min(Math.max(currentZoom, 16), 18));

      if (options?.openInfo === false) return;

      const cacheKey = addressCacheKey(store);
      const cachedAddress = addressCacheRef.current.get(cacheKey);
      const initialAddress = cachedAddress || buildFallbackAddress(store);
      const distanceKm = storeDistancesRef.current?.[store.id];

      info.setContent(buildInfoWindowHtml(store, initialAddress, distanceKm));
      info.open(map, marker);
      tuneInfoWindowDom();

      if (!cachedAddress) {
        reverseGeocodeLatLng(pos.lat(), pos.lng()).then((formattedAddress) => {
          if (!formattedAddress) return;
          addressCacheRef.current.set(cacheKey, formattedAddress);
          writeAddressSessionCache(addressCacheRef.current);
          info.setContent(
            buildInfoWindowHtml(
              store,
              formattedAddress,
              storeDistancesRef.current?.[store.id],
            ),
          );
          tuneInfoWindowDom();
        });
      }
    },
    [applyMarkerFocus],
  );
  const storeQueries = useMemo(
    () =>
      debouncedStores.map((s) => ({
        store: s,
        query: parseQuery(s),
      })),
    [debouncedStores],
  );

  /** ================= INIT MAP ================= */
  useEffect(() => {
    if (!containerRef.current || isPrerender()) return;

    let cancelled = false;

    const init = async () => {
      await loadScript();
      await waitMap();
      if (cancelled) return;

      const mapOptions: Record<string, unknown> = {
        center: DEFAULT_CENTER,
        zoom: 12,
        mapTypeId: "roadmap",
        // JSON `styles` are not supported on vector maps; force raster so night
        // theme tiles stay stable while zooming (avoids "no imagery" gaps).
        maxZoom: 18,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        // One-finger pan on mobile (avoids "Use two fingers to move the map").
        gestureHandling: "greedy",
        styles: stylesForMapTheme(mapTheme),
      };

      const RenderingType = window.google?.maps?.RenderingType;
      if (RenderingType?.RASTER) {
        mapOptions.renderingType = RenderingType.RASTER;
      }

      const map = new window.google.maps.Map(containerRef.current, mapOptions);

      mapRef.current = map;
      injectInfoWindowStyles();
      infoRef.current = new window.google.maps.InfoWindow({ maxWidth: 280 });
      infoRef.current.addListener("domready", tuneInfoWindowDom);

      setMapReady(true);
      // clusterRef.current = new MarkerClusterer({
      //   map,
      //   markers: [],
      // });
    };

    init();

    return () => {
      cancelled = true;
    };
    // mapTheme is applied in a separate effect after init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    map.setOptions({
      mapTypeId: "roadmap",
      styles: stylesForMapTheme(mapTheme),
    });
  }, [mapReady, mapTheme]);

  useEffect(() => {
    if (!mapReady || !infoRef.current) return;

    const info = infoRef.current;
    const listener = info.addListener("closeclick", () => {
      const focusedId = focusedStoreIdRef.current;
      applyMarkerFocus(undefined);
      fitMapToStores(Boolean(userPosition));
      if (focusedId !== undefined) {
        onMarkerClickRef.current?.(focusedId);
      }
    });

    return () => {
      window.google?.maps?.event.removeListener(listener);
    };
  }, [mapReady, applyMarkerFocus, fitMapToStores, userPosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !window.google?.maps) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
    if (radiusCircleRef.current) {
      radiusCircleRef.current.glow.setMap(null);
      radiusCircleRef.current.main.setMap(null);
      radiusCircleRef.current = null;
    }

    if (!userPosition) return;

    userMarkerRef.current = new window.google.maps.Marker({
      position: userPosition,
      map,
      title: "Your location",
      zIndex: window.google.maps.Marker.MAX_ZINDEX + 3,
      icon: getUserLocationMarkerIcon(),
      optimized: false,
    });

    if (nearbyRadiusKm) {
      const isNight = mapTheme === "night";
      const color = isNight ? "#F05A2A" : "#4285F4";
      const radiusMeters = nearbyRadiusKm * 1000;

      const glow = new window.google.maps.Circle({
        map,
        center: userPosition,
        radius: radiusMeters,
        fillOpacity: 0,
        strokeColor: color,
        strokeOpacity: isNight ? 0.3 : 0.25,
        strokeWeight: 6,
        clickable: false,
        zIndex: 1,
      });

      const main = new window.google.maps.Circle({
        map,
        center: userPosition,
        radius: radiusMeters,
        fillColor: color,
        fillOpacity: isNight ? 0.1 : 0.08,
        strokeColor: color,
        strokeOpacity: isNight ? 0.6 : 0.5,
        strokeWeight: 1,
        clickable: false,
        zIndex: 2,
      });

      radiusCircleRef.current = { glow, main };
    }
  }, [mapReady, mapTheme, nearbyRadiusKm, userPosition]);

  /** ================= UPDATE MARKERS ================= */
  useEffect(() => {
    if (!mapReady) return;
    if (!mapRef.current) return;
    if (renderLockRef.current) return;

    renderLockRef.current = true;
    setLoading(true);

    const run = async () => {
      const map = mapRef.current;
      const storeGeoCache = storeGeoCacheRef.current;

      const newMarkers = new Map<number, any>();
      const bounds = new window.google.maps.LatLngBounds();

      /** ⚡ 并行 geocode（不卡 + 不亂序） */
      const results = await Promise.all(
        storeQueries.map(async ({ store, query }) => {
          let pos: MapLatLng | null = null;

          /** ① priority cache */
          if (storeGeoCache.has(store.id)) {
            pos = storeGeoCache.get(store.id)!;
          }

          /** ② fallback */
          if (!pos) {
            if (store.lat != null && store.lng != null) {
              pos = { lat: store.lat, lng: store.lng };
            } else {
              pos = await geocode(query);
            }

            if (pos) storeGeoCache.set(store.id, pos);
          }

          return pos ? { store, pos, query } : null;
        }),
      );

      /** ⚡ render markers */
      for (const item of results) {
        if (!item) continue;

        const { store, pos } = item;
        storesByIdRef.current.set(store.id, store);

        let marker = markersRef.current.get(store.id);

        if (!marker) {
          marker = new window.google.maps.Marker({
            position: pos,
            title: store.title,
            map,
          });

          marker.addListener("click", () => {
            const latestStore = storesByIdRef.current.get(store.id) ?? store;
            focusStoreOnMap(latestStore, marker);
            onMarkerClickRef.current?.(store.id);
          });
        } else {
          marker.setPosition(pos);
          marker.setMap(map);
          marker.setTitle(store.title);
          window.google.maps.event.clearListeners(marker, "click");
          marker.addListener("click", () => {
            const latestStore = storesByIdRef.current.get(store.id) ?? store;
            focusStoreOnMap(latestStore, marker);
            onMarkerClickRef.current?.(store.id);
          });
        }

        newMarkers.set(store.id, marker);
        bounds.extend(pos);
      }

      /** ⚡ cleanup removed markers */
      markersRef.current.forEach((m, id) => {
        if (!newMarkers.has(id)) m.setMap(null);
      });

      markersRef.current = newMarkers;
      boundsRef.current = bounds;

      if (focusedStoreIdRef.current) {
        applyMarkerFocus(focusedStoreIdRef.current);
      }

      /** ⚡ IMPORTANT: fit map to visible stores (and user when nearby mode is on) */
      if (!selectedStoreId) {
        fitMapToStores(Boolean(userPosition));
      }

      renderLockRef.current = false;
      setLoading(false);
    };

    run();

    return () => {
      renderLockRef.current = false;
    };
  }, [
    mapReady,
    storeQueries,
    selectedStoreId,
    userPosition,
    applyMarkerFocus,
    fitMapToStores,
    focusStoreOnMap,
  ]);
  /** ================= SELECTED ================= */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!selectedStoreId) {
      applyMarkerFocus(undefined);
      infoRef.current?.close();
      fitMapToStores(Boolean(userPosition));
      return;
    }

    const store = storesByIdRef.current.get(selectedStoreId);
    const marker = markersRef.current.get(selectedStoreId);
    if (!store || !marker) return;

    focusStoreOnMap(store, marker);
  }, [
    selectedStoreId,
    userPosition,
    applyMarkerFocus,
    fitMapToStores,
    focusStoreOnMap,
  ]);

  if (isPrerender()) {
    return <div className="h-[400px]">Map preview</div>;
  }

  return (
    <div>
      <div
        ref={containerRef}
        className="w-full md:h-[500px] h-[400px] rounded-xl border"
      />

      {loading && (
        <div className="text-sm text-gray-500 mt-2">loading stores...</div>
      )}
    </div>
  );
};

export default RegionalStoreMap;
